import React from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'
import Highcharts from 'highcharts'

import { createTheme, ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'

import translationsEN from './resources/en-US/translation.json'
import LoginView from './views/login'
import MainView from './views/main'

import Navbar from './components/navbar/navbar'
import ProfilesView from './views/profiles'
import LogsView from './views/logs'
import LiveView from './views/live'
import LogoutView from './views/logout'
import LogView from './views/log'
import Dataloader from './utils/dataloader'
import SettingsView from './views/settings'
import CreateProfileView from './views/createProfile'
import FlatpageView from './views/flatpage'
import InventoriesView from './views/inventories'
import InventoryDetailView from './views/inventoryDetail'
import CuppingsView from './views/cuppings'
import NewCuppingView from './views/newCupping'
import CuppingDetailView from './views/cuppingDetail'
import RegisterCuppingView from './views/registerCupping'
import ScheduleView from './views/schedule'
import ScheduleDetailView from './views/scheduleDetail'
import NotFoundView from './views/notfound'
import ProfileLibrary from './views/profileLibrary'
import SignupView from './views/signup'
import { ThemeSettings } from './utils/theme'
import { useContextHook } from './store/context'
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'

i18n.use(initReactI18next).init({
  resources: {
    'en-US': {
      translation: translationsEN,
    },
  },
  lng: 'en-US',
  fallbackLng: 'en-US',

  interpolation: {
    escapeValue: false,
  },
})

function App(): JSX.Element {
  const { state } = useContextHook()

  const systemPrefersDark = useMediaQuery(
    {
      query: '(prefers-color-scheme: dark)',
    },
    undefined,
    (prefersDark: boolean) => {
      setIsDarkTheme(prefersDark)
    }
  )

  const [isDarkTheme, setIsDarkTheme] = React.useState<boolean>(systemPrefersDark)

  React.useEffect(() => {
    if (systemPrefersDark || state.user?.is_darkmode) {
      setIsDarkTheme(true)
      Highcharts.setOptions(ThemeSettings.highchartsDarkTheme)
    } else {
      setIsDarkTheme(false)
      Highcharts.setOptions(ThemeSettings.highchartsLightTheme)
    }
  }, [state.user, systemPrefersDark])

  return (
    <ThemeProvider
      theme={createTheme({
        palette: {
          ...(isDarkTheme ? ThemeSettings.darkTheme : ThemeSettings.lightTheme),
        },
        ...ThemeSettings.getStyleOverrides(isDarkTheme),
      })}
    >
      <CssBaseline />
      <BrowserRouter>
        <Navbar />
        <Dataloader />
        <Routes>
          <Route path="/machine/:machineId/profiles/create" element={<CreateProfileView />} />
          <Route path="/machine/:machineId/profiles/:profileId" element={<CreateProfileView />} />
          <Route path="/machine/:machineId/profiles/" element={<ProfilesView />} />
          <Route path="/machine/:machineId/logs/:logId" element={<LogView />} />
          <Route path="/machine/:machineId/logs/" element={<LogsView />} />
          <Route path="/machine/:machineId/live" element={<LiveView />} />
          <Route path="/machine/:machineId/schedules/create" element={<ScheduleDetailView />} />
          <Route path="/machine/:machineId/schedules/:scheduleId" element={<ScheduleDetailView />} />
          <Route path="/machine/:machineId/schedules" element={<ScheduleView />} />
          <Route path="/inventories/create" element={<InventoryDetailView />} />
          <Route path="/inventories/:inventoryId" element={<InventoryDetailView />} />
          <Route path="/inventories" element={<InventoriesView />} />
          <Route path="/cuppings/addscore/:cuppingGuid" element={<RegisterCuppingView />} />
          <Route path="/cuppings/:cuppingId" element={<CuppingDetailView />} />
          <Route path="/cuppings/create" element={<NewCuppingView />} />
          <Route path="/cuppings/" element={<CuppingsView />} />
          <Route path="/login" element={<LoginView />} />
          <Route path="/logout" element={<LogoutView />} />
          <Route path="/setup" element={<SettingsView />} />
          <Route path="/shared_profile/:sharedProfileId" element={<CreateProfileView />} />
          <Route path="/signup/:inviteId" element={<SignupView />} />
          <Route path="/support/:flatpageurl" element={<FlatpageView />} />
          <Route path="/profilelibrary" element={<ProfileLibrary />} />
          <Route path="/" element={<MainView />} />
          <Route path="*" element={<NotFoundView />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  )
}

export default App
